import { Link, useLocation } from "react-router-dom";
import { LandingHeader } from "@/components/site/LandingHeader";
import { useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { REGIONS } from "@/lib/format";
import { SocialIcons } from "@/components/site/SocialIcons";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  if (location.pathname === "/") return <LandingHeader />;

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 border-b bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70"
    >
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6 lg:px-8">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2 transition-transform hover:scale-[1.03]">
          <picture><source media="(max-width: 1023px)" srcSet="/logo-wordmark.webp" /><img src="/logo.png" alt="Best Escape Holidays - By N V Hospitalities" className="h-auto w-44 drop-shadow-sm lg:h-14 lg:w-auto" /></picture>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {REGIONS.map((r) => (
            <Link
              key={r}
              to={`/packages?region=${r}`}
              data-testid={`navbar-region-link-${r.toLowerCase()}`}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-[hsl(var(--brand-primary))]"
            >
              {r}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <SocialIcons className="flex items-center gap-1.5 text-slate-500" iconClassName="h-4 w-4" />
          <a
            href="tel:+919816672006"
            data-testid="navbar-contact-button"
            className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[hsl(var(--brand-primary))]"
          >
            <Phone className="h-4 w-4" /> 98166 72006
          </a>
          <Link to="/admin/login" data-testid="navbar-admin-link">
            <Button variant="outline" size="sm">Admin</Button>
          </Link>
          <Link to="/packages" data-testid="navbar-explore-button">
            <Button size="sm" className="bg-[hsl(var(--brand-accent))] text-slate-900 hover:bg-[hsl(var(--brand-accent))]/90">
              Explore Packages
            </Button>
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-11 w-11 lg:hidden" data-testid="navbar-mobile-menu-button" aria-label="Open navigation menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" data-testid="mobile-navigation-menu" closeTestId="navbar-menu-close" closeLabel="Close navigation" className="w-80 max-w-full overflow-y-auto">
            <SheetTitle className="pr-12">Explore Best Escape</SheetTitle><SheetDescription className="mt-2">Your next holiday starts here.</SheetDescription>
            <div className="mt-5 flex flex-col gap-2">
              {REGIONS.map((r) => (
                <Link
                  key={r}
                  to={`/packages?region=${r}`}
                  onClick={() => setOpen(false)}
                  data-testid={`navbar-mobile-region-link-${r.toLowerCase()}`}
                  className="flex min-h-11 items-center rounded-md px-2 text-base font-medium text-slate-700 hover:bg-primary/5"
                >
                  {r} Packages
                </Link>
              ))}
              <div className="my-2 h-px bg-border" />
              <Link to="/packages" onClick={() => setOpen(false)} data-testid="navbar-mobile-explore-link">
                <Button className="min-h-11 w-full bg-[hsl(var(--brand-accent))] text-slate-900 hover:bg-[hsl(var(--brand-accent))]/90">
                  Explore All Packages
                </Button>
              </Link>
              <a href="tel:+919816672006" data-testid="navbar-mobile-phone" className="flex min-h-11 items-center gap-2 text-base font-medium text-slate-600">
                <Phone className="h-4 w-4" /> 98166 72006
              </a>
              <SocialIcons className="flex items-center gap-2 text-slate-500" iconClassName="h-4 w-4" />
              <Link to="/admin/login" onClick={() => setOpen(false)} data-testid="navbar-mobile-admin-link" className="flex min-h-11 items-center text-sm text-slate-500">
                Admin Login
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
